import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { ImagePlus } from 'lucide-react';
import { getInitials } from '../utils/customerDisplay';

const Wrap = styled.div`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: ${({ $size }) => Math.max(8, Math.round($size / 5))}px;
  overflow: hidden;
  background: ${({ $hasLogo }) => ($hasLogo ? 'var(--white-a66)' : 'var(--green-a08)')};
  border: 1px solid var(--shadow-a05);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'pointer')};
  user-select: none;
  flex: 0 0 auto;
  outline: ${({ $dragOver }) => ($dragOver ? '2px dashed var(--green-800)' : 'none')};
  outline-offset: ${({ $dragOver }) => ($dragOver ? '2px' : 0)};

  &:focus-visible {
    outline: 2px solid var(--green-800);
    outline-offset: 2px;
  }
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 0.25rem;
  box-sizing: border-box;
  pointer-events: none;
`;

const Initials = styled.div`
  font-family: var(--font-heading, sans-serif);
  font-weight: 800;
  font-size: ${({ $size }) => Math.round($size / 3.2)}px;
  color: var(--green-800);
  letter-spacing: 0.02em;
  pointer-events: none;
`;

const HoverChip = styled.div`
  position: absolute;
  inset: 0;
  background: var(--shadow-a45);
  color: var(--white);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: ${({ $size }) => Math.max(10, Math.round($size / 6))}px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: ${({ $forceShow }) => ($forceShow ? 1 : 0)};
  transition: opacity 120ms ease;
  pointer-events: none;

  ${Wrap}:hover & {
    opacity: 1;
  }
  ${Wrap}:focus-visible & {
    opacity: 1;
  }
`;

const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;

export default function CustomerLogoUpload({
  customerName = '',
  logoUrl = null,
  onFileSelected,
  size = 64,
  readOnly = false,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const initials = getInitials(customerName) || '?';
  const hasLogo = Boolean(logoUrl);

  const openPicker = () => {
    if (readOnly) return;
    inputRef.current?.click();
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onFileSelected) onFileSelected(file);
    if (e.target) e.target.value = '';
  };

  const handleKeyDown = (e) => {
    if (readOnly) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (readOnly) return;
    const file = e.dataTransfer?.files?.[0];
    if (file && onFileSelected) onFileSelected(file);
  };

  const handleDragEnter = (e) => {
    if (readOnly) return;
    e.preventDefault();
    setDragOver(true);
  };
  const handleDragOver = (e) => {
    if (readOnly) return;
    e.preventDefault();
  };
  const handleDragLeave = () => {
    setDragOver(false);
  };

  return (
    <Wrap
      $size={size}
      $hasLogo={hasLogo}
      $readOnly={readOnly}
      $dragOver={dragOver}
      role={readOnly ? undefined : 'button'}
      tabIndex={readOnly ? -1 : 0}
      aria-label={readOnly ? undefined : hasLogo ? 'Replace logo' : 'Upload logo'}
      onClick={openPicker}
      onKeyDown={handleKeyDown}
      onDrop={handleDrop}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      title={readOnly ? undefined : hasLogo ? 'Click to replace logo' : 'Click to upload logo'}
    >
      {hasLogo ? (
        <Img src={logoUrl} alt={`${customerName} logo`} />
      ) : (
        <Initials $size={size}>{initials.slice(0, 2)}</Initials>
      )}
      {!readOnly && (
        <HoverChip $size={size} $forceShow={dragOver}>
          <ImagePlus size={Math.max(12, Math.round(size / 5.5))} aria-hidden="true" />
          {hasLogo ? 'Replace' : 'Upload'}
        </HoverChip>
      )}
      <HiddenFileInput
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={handleChange}
        tabIndex={-1}
        aria-hidden="true"
      />
    </Wrap>
  );
}
